import React, { useContext, useEffect, useState, useRef } from "react";
import { useIdleTimer } from "react-idle-timer";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import _ from "lodash";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
// import Alert from "react-s-alert";
// import "react-s-alert/dist/s-alert-default.css";
// import "react-s-alert/dist/s-alert-css-effects/slide.css";
import {
   Attachments, CartMenu, ForgotPassword, Home, IdleModal, MobileDeviceSelect, MobileHome, MobileTest,
   ResetPassword, SignIn, SignInQRCode, UpdatePassword, UserProfile,
} from "./components";
import { Footer, SidebarMenu } from "./layouts";
import { api, constants, helpers, routeLists, storage, UserContext } from "./utils";
import { useActiveCustomer, useLocalStorage } from "./hooks";

const { ENDPOINTS } = constants;

function getThemeFromWindow() {
   const windowDarkModePreference = window.matchMedia(
      "(prefers-color-scheme:dark)"
   ).matches;
   return windowDarkModePreference
      ? constants.THEMES.darkTheme.label
      : constants.THEMES.default.label;
}

export default function App() {
   const userCtx = useContext(UserContext);
   const currentDOMRef = useRef(null);
   const navigate = useNavigate();
   const defaultPinned = { label: "Desktop or Laptop", value: 0, pinned: true };
   const defaultSavedDevice = { isMobile: false };

   const [adminTheme, setAdminTheme] = useLocalStorage("adminTheme", null, false);
   const [adminUser, setAdminUser] = useLocalStorage("adminUser", null);
   const [adminUserToken, setAdminUserToken] = useLocalStorage("adminToken", null, false);
   const [appVersionNumber, setAppVersionNumber] = useLocalStorage("app-version", "", false);
   // eslint-disable-next-line no-unused-vars
   const [lastUsername, setLastUserName] = useLocalStorage("lastUsername", null, false);
   const [loggedOnAt, setLoggedOnAt] = useLocalStorage("loggedOnAt", 0, false);
   const [onMobile, setOnMobile] = useLocalStorage("onMobile", null, false);
   const [parsedUser, setParsedUser] = useLocalStorage("currentUser", null);
   const [savedDevice, setSavedDevice] = useLocalStorage("deviceType", defaultSavedDevice);
   const [savedPinned, setSavedPinned] = useLocalStorage("pinnedItem", defaultPinned);
   // eslint-disable-next-line no-unused-vars
   const [tethered, setTethered] = useLocalStorage("untethering", null, false);
   const [theme, setTheme] = useLocalStorage("theme", null, false);
   const [tokenFromStorage, setTokenFromStorage] = useLocalStorage("token", null, false);

   const [activeCarts, setActiveCarts] = useState([]);
   const [authToken, setAuthToken] = useState(tokenFromStorage);
   const [cartMenuOpen, setCartMenuOpen] = useState(false);
   // Seed the active customer from the persisted account on load, for CUSTOMER roles only. This lives
   // here (not just in CustomerDashboard) so every screen has the right account on FIRST render - the
   // picker's switch does a full reload, and without this a reload landing on any non-dashboard screen
   // would read null and fall back to the primary, silently ignoring the switch (e.g. the order page
   // and the cart would target the primary while the picker showed the selected account). A synchronous
   // initializer, not an effect, so there is no window where screens read null and no loop with the
   // dashboard's own resolver. Internal users are untouched: their active customer is a dashboard
   // browsing selection, not an authorization scope, so it stays null until they pick one.
   const [customerFilter, setCustomerFilter] = useState(() => {
      if (!parsedUser?.isAnyCustomerTypeRole) return null;
      try {
         const raw = storage.getItem(constants.LocalStorageKeys.Customer);
         if (raw && raw !== "null") return JSON.parse(raw);
      } catch { /* fall through to the primary */ }
      return parsedUser.customerId
         ? { label: parsedUser.customerId, value: parsedUser.customerId }
         : null;
   });
   const [impersonating, setImpersonating] = useState(adminUser ? true : false);
   // The signed-in customer's own ACTIVE assigned accounts, for the app-wide account picker. Fetched
   // once here rather than inside the picker, which DesktopContainer renders on every screen - that
   // would re-request on every navigation.
   const [myCustomerAccounts, setMyCustomerAccounts] = useState([]);
   const [isIdle, setIsIdle] = useState(false);
   const [openLineItem, setOpenLineItem] = useState(null);
   const [pinnedState, setPinnedState] = useState(savedPinned.pinned || false);
   const [routes, setRoutes] = useState(resolveRoutes());
   const [shoppingCart, setShoppingCart] = useState(null);
   // Active-customer persistence (the 60-day `Customer` cookie + its localStorage twin). Owned here
   // rather than in CustomerDashboard so any screen can restore or persist the active account - the
   // prerequisite for an app-wide account picker (multi-account plan 5.1).
   const activeCustomer = useActiveCustomer(parsedUser, impersonating);
   const idleTimer = useIdleTimer({
      onPrompt: () => setIsIdle(true),
      onIdle: () => {
         if (!parsedUser) return;
         signOut("Your session has timed out due to inactivity. Please log in.");
      },
      timeout: constants.TIMERS.oneHundredFifteenMinutes,
      promptBeforeIdle: constants.TIMERS.fiveMinutes,
      startOnMount: false,
      startManually: true,
      stopOnIdle: true,
   });
   const nowTime = () => {
      return new Date().getTime();
   };
   const showSidebars =
      userCtx &&
      parsedUser &&
      parsedUser.id &&
      !parsedUser.mustChangePassword &&
      savedDevice;

   let savedTheme = null;

   if (typeof loggedOnAt !== 'number') {
      setLoggedOnAt(parseInt(loggedOnAt, 10) || 0);
   }

   if (parsedUser && parsedUser.typeOfTheme) {
      savedTheme = _.find(
         constants.THEMES,
         (t) => t.value === parseInt(parsedUser.typeOfTheme)
      )?.label;
   }

   if (!savedTheme && theme) {
      savedTheme = theme;
   }

   if (!savedTheme) {
      savedTheme = getThemeFromWindow();
   }

   useEffect(() => {
      verifyAuthentication();
      setIsIdle(false);
      // below line stops the "React Hook useEffect has missing dependencies" eslint error
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      if (!parsedUser) return;
      if (isIdle) return; // if the user toggles it then this should be false else
      idleTimer.start(); // idleTimer.reset(); // the useEffect is running off the onPrompt idleTimer
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isIdle]); // thus we don't wish to reset the timer and instead wait for a time out

   useEffect(() => {
      setRoutes(resolveRoutes());
   }, [parsedUser]);

   useEffect(() => {
      // Load the customer's ACTIVE assigned accounts for the picker. Customer roles ONLY:
      // Customer/MyActiveAccounts is gated AnyCustomer, and the global axios handler toasts on 403, so
      // calling this for an internal user would put a "no permission" toast on every page - the same
      // trap the notification-bell poll hit. Not read from currentUser.customerIds because that
      // projection carries retired accounts too (it has no active filter, and cannot have one - active
      // lives in GP, not in UserCustomer), and offering a retired account fails closed with no error.
      if (!parsedUser?.isAnyCustomerTypeRole) {
         setMyCustomerAccounts([]);
         return;
      }
      api
         .fetch(`Customer/MyActiveAccounts`)
         .then((response) => {
            setMyCustomerAccounts(response?.data?.success ? (response.data.accounts ?? []) : []);
         })
         .catch((error) => {
            api.catchHandler(error);
            setMyCustomerAccounts([]);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [parsedUser?.id, parsedUser?.isAnyCustomerTypeRole]);

   useEffect(() => {
      // A tab that stays signed in across a release never re-hits Public/Authenticate, so the sidebar
      // it stored at sign-in goes stale on a new menu item or a role change (localStorage survives the
      // version-check reload). Re-fetch once per token change and only touch state when the menus
      // actually differ, so an unchanged sidebar causes no re-render. A failed fetch just leaves the
      // stored menus as they were: the api.js interceptor resolves HTTP errors rather than rejecting,
      // so they arrive here without data.success and the update is skipped. (A 401 still signs the user
      // out through that interceptor, as it would on any call.)
      // The cleanup drops a response that lands after the token changed: impersonation or sign-out
      // mid-request would otherwise write the previous user's menus over the new one. Merge onto the
      // user as it is stored NOW, not as this render captured it.
      // An account with no roles is skipped: the server 403s MyMenus for it (RoleRequiredFilter), and it
      // has no menus to refresh. Read from storage for the same reason as the merge below - the setters
      // write it synchronously, so it is already the user this token belongs to.
      if (!authToken || helpers.hasNoRoles(helpers.getCurrentUser())) return;
      let cancelled = false;
      api
         .fetch(`Reference/MyMenus`)
         .then((response) => {
            if (cancelled) return;
            const menus = response?.data?.success ? response.data.menus : null;
            const current = helpers.getCurrentUser();
            if (menus && current && !_.isEqual(menus, current.menus)) {
               setParsedUser({ ...current, menus });
            }
         })
         .catch(api.catchHandler);
      return () => {
         cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [authToken]);

   useEffect(() => {
      if (currentDOMRef && currentDOMRef.current) {
         let rootNode = currentDOMRef.current.parentNode;
         if (rootNode && rootNode.parentNode) {
            rootNode.parentNode.className = `${theme}Body`;
         }
      }
   }, [theme]);

   const clearImpersonation = () => {
      if (!impersonating) {
         console.warn("impersonate is false in clearImpersonation");
         return;
      }

      // clear localstorage
      setTokenFromStorage(adminUserToken);
      setParsedUser(adminUser);
      // Drop the impersonated customer's active filter; the admin's own dashboard restores theirs.
      setCustomerFilter(null);
      setAuthToken(adminUserToken);
      setTheme(adminTheme);
      setTethered(true);
      loadActiveCarts();
      storage.removeItem("adminToken");
      storage.removeItem("adminUser");
      setImpersonating(false);
      navigate("/");
   }

   function enhanceUser(u) {
      if (u) {
         u.includeSystemAdministrator = helpers.hasAdminRole(u.roleTypeIdList);
      }
      return u;
   }

   function impersonate(user, impersonateToken, menus) {
      if (impersonating) {
         return;
      }
      setAdminUser(parsedUser);
      setAdminUserToken(tokenFromStorage);
      setAdminTheme(theme);
      user = enhanceUser(user);
      user.menus = menus;
      setTokenFromStorage(impersonateToken);
      setParsedUser(user);
      // Reset the active-customer context to the impersonated user's OWN account so the admin's
      // last-used customer doesn't bleed into the impersonated session (that broke the customer
      // dashboard with a cross-customer "no permission").
      setCustomerFilter(user.isAnyCustomerTypeRole ? { label: user.customerId, value: user.customerId } : null);
      if (user.isAnyCustomerTypeRole) {
         loadCart(user.customerId);
      }
      setAuthToken(impersonateToken);
      setImpersonating(true);
      navigate("/");
   }

   function loadActiveCarts() {
      if (parsedUser) {
         api
            .fetch(`Cart/ActiveCarts`, { userId: parsedUser.id })
            .then((response) => {
               if (response.data.success) {
                  setActiveCarts(response.data.activeCarts);
               } else {
                  setActiveCarts([]);
               }
            })
            .catch(api.catchHandler);
         setShoppingCart({});
      }
   }

   function loadCart(customerId) {
      api
         .fetch(`Cart/GetCartByUserAndCustomer`, { customerId: customerId })
         .then((response) => {
            if (response.data.success) {
               setShoppingCart(response.data.cart);
               setActiveCarts(response.data.activeCarts);
            } else {
               setShoppingCart({});
               setActiveCarts([]);
            }
         })
         .catch(api.catchHandler);
   }

   function processSignOut(message) {
      storage.removeItem("token");
      storage.removeItem("currentUser");
      storage.removeItem("lastActivity");
      setLoggedOnAt(nowTime());
      storage.removeItem("pinnedItem"); // when the user logs out this should not be needed
      setParsedUser(null);
      setAuthToken(null);
      setPinnedState(false);
      setTheme(theme || getThemeFromWindow());
      if (message) {
         toast.warning(message);
      }
      navigate("/login");
   }

   function resolveRoutes() {
      return _.chain(routeLists)
         .reject(_.isNil)
         .uniqBy("key")
         .value();
   }

   // verifyServerActivity gates whether the logout is confirmed against the server's activity record
   // first. It must stay TRUE for the idle-timeout path (don't log out a user who is still active in
   // another tab), but the EXPLICIT Sign Out button passes FALSE: a user click should always do exactly
   // what the button says - stop impersonating, or log out - never be silently cancelled. With
   // EnableCache off (as in dev), ActivityCheck always reports "active", so a verified sign-out from the
   // button would never fire and the only way out was to delete the token by hand.
   function signOut(message, verifyServerActivity = true) {
      if (impersonating) {
         clearImpersonation();
      } else {
         if (verifyServerActivity) {
            api.fetch("Public/ActivityCheck").then(r => {
               if (r.data.success === false) {
                  processSignOut(message);
               } else {
                  setIsIdle(false);
               }
            }).catch(e => {
               console.error("Failed activity check", e);
               processSignOut(message);
            });
         } else {
            processSignOut(message);
         }
      }
   }

   function toggleCartOpen(openOrClose) {
      setCartMenuOpen(openOrClose);
      if (!openOrClose) {
         setOpenLineItem(null);
      }
   }

   const user = {
      currentUser: parsedUser,
      appVersion: appVersionNumber,
      impersonating: impersonating,
      impersonate: impersonate,
      clearImpersonation: clearImpersonation,
      signIn: (newUser, token, menus) => {
         // Customers and accounts with no roles always land on the desktop home page; a role-less
         // account has nothing to open in either mobile UI.
         const noRoles = helpers.hasNoRoles(newUser);
         const deviceIsDesktop = newUser.isAnyCustomerTypeRole || noRoles || (!savedDevice.isMobile && !onMobile);
         const route = deviceIsDesktop ? "/" : "/mobile";
         const device = deviceIsDesktop ? constants.PINNED_TYPES.DeskTop : constants.PINNED_TYPES.Mobile;
         if (token) {
            setTokenFromStorage(token);
            setAuthToken(token);
            // A real credential login is never a continuation of impersonation. Clear any stale
            // impersonation artifacts so `impersonating` does not re-initialize true on the next mount
            // (it is seeded from adminUser). Left behind, it silently disables active-account
            // persistence (the `if (impersonating) return` guard in useActiveCustomer.persist), which
            // freezes the Customer cookie on the admin's last account and reverts the picker on every
            // load. This state is orphaned whenever the impersonation token is dropped without going
            // through Stop-Impersonating - a manual token delete, or a session-timeout 401 mid-session.
            setAdminUser(null);
            setAdminUserToken(null);
            setImpersonating(false);
         }
         newUser = enhanceUser(newUser);
         setTheme(newUser.typeOfTheme === constants.THEMES.darkTheme.value
            ? constants.THEMES.darkTheme.label
            : constants.THEMES.lightTheme.label);
         setLastUserName(newUser.username);
         if (menus) {
            newUser.menus = menus;
         }
         setLoggedOnAt(nowTime());
         setPinnedState(savedPinned?.pinned);
         setParsedUser(newUser);
         setSavedPinned(device);
         if (newUser.isAnyCustomerTypeRole) {
            // A real credential sign-in RESETS the active account to the user's primary (plan 5.1).
            // The 60-day `Customer` cookie governs the rest of the session, but a FRESH login should
            // land on the primary rather than on whatever account was last selected - possibly weeks
            // ago, possibly by a different person on a shared machine. Login (step 6) has already
            // healed the primary to an ACTIVE account server-side, so newUser.customerId is the right
            // target here and needs no further validation.
            //
            // ONLY on the token-bearing branch: signIn is also called WITHOUT a token to refresh the
            // stored user mid-session (UserProfile save, UpdatePassword), and resetting there would
            // throw a customer back to their primary for merely editing their profile, discarding the
            // account they had selected. Internal users are deliberately untouched - their active
            // customer is a browsing convenience, not an authorization scope, so their last-used
            // customer keeps its existing 60-day persistence.
            if (token && newUser.customerId) {
               const primary = { label: newUser.customerId, value: newUser.customerId };
               setCustomerFilter(primary);
               activeCustomer.persist(primary);
            }
            loadCart(newUser.customerId);
         } else if (!noRoles) {
            // Cart/ActiveCarts 403s for an account with no roles, so it is not loaded for one.
            loadActiveCarts();
         }
         idleTimer.start();
         // New mobile (/m) deep-link return: if the /m app stashed a return path on
         // a 401, send the user back there after re-auth. Consume it either way
         // (one-shot). Read RAW (mReturnPath is not JSON); guard to "/m/" (same-origin,
         // no open redirect); use window.location because /m is a separate SPA.
         const mReturnPath = localStorage.getItem("mReturnPath");
         if (mReturnPath) localStorage.removeItem("mReturnPath");
         // Never for an account with no roles - it is kept on the desktop home page (see deviceIsDesktop).
         if (mReturnPath && mReturnPath.startsWith("/m/") && !noRoles) {
            window.location.assign(mReturnPath);
            return;
         }
         navigate(route);
      },
      signOut: signOut,
      theme: theme,
      changeTheme: setTheme,
      customerFilter,
      setCustomerFilter,
      activeCustomer,
      myCustomerAccounts,
      shoppingCart,
      setShoppingCart,
      activeCarts,
      setActiveCarts,
      cartMenuOpen,
      setCartMenuOpen,
      openLineItem,
      setOpenLineItem,
      loadCart,
      loadActiveCarts,
      toggleCartOpen,
      onMobile,
      setOnMobile,
      setAppVersionNumber,
      pinnedState,
      setPinnedState,
      setPinned: (switchToMobile, switchingBackToDeskTop = false) => {
         if (switchToMobile) {
            setPinnedState(false);
            setSavedPinned(constants.PINNED_TYPES.Mobile);
            return;
         }
         if (switchingBackToDeskTop) {
            setPinnedState(true);
            setSavedPinned(constants.PINNED_TYPES.DeskTop);
            return;
         }
         // This else branch can be omitted because the return statements above
         // will prevent this code from being executed if either of the above conditions are true.
         const nextState = !pinnedState;
         setPinnedState(nextState);
         setSavedPinned(nextState ? constants.PINNED_TYPES.DeskTop : constants.PINNED_TYPES.DeskTopUpinned);
      },
      savedDevice,
      setSavedDevice
   };

   function verifyAuthentication() {
      const nw = nowTime();
      if (!authToken
         || !loggedOnAt
         || (!window.lastVerifiedAuthAt || ((nw - window.lastVerifiedAuthAt) < constants.TIMERS.fifteenSeconds))
         || (nw - loggedOnAt) < constants.TIMERS.twentyMinutes
      )
         return; // not logged in, (no need to verify anything) or < 20 min since logon or last verified < 15s ago
      window.lastVerifiedAuthAt = nw;
      api.fetch("Public/AuthCheck").then(r => {
         if (r.data.success === false) {
            signOut(r.data.message, false);
         }
      }).catch(e => {
         console.error("Failed auth check", e);
         signOut("Your session has timed out due to inactivity. Please log in.", false);
      });
   }

   return (
      <div className={`${theme}`} ref={currentDOMRef}>
         <ToastContainer
            className='toast-multiline'
            position="top-right"
            autoClose={15000}
            limit={1}
            hideProgressBar={false}            
         />
         <IdleModal
            isOpen={isIdle}
            toggle={() => {
               setIsIdle(false);
            }}
         />
         <UserContext.Provider value={user}>
            {showSidebars ? (
               <>
                  <SidebarMenu />
                  <CartMenu />
               </>
            ) : null}
            <div id="desktop-routes">
               {authToken !== null ? (
                  // all authorized routes
                  <>
                     {helpers.mustChangePassword(parsedUser) ? (
                        <Routes>
                           <Route exact path="/*" element={<UpdatePassword />} />
                        </Routes>
                     ) : (
                        <Routes>
                           <Route exact path="/" element={<Home />} />
                           <Route exact path={ENDPOINTS.mobileHome} element={<MobileHome />} />
                           <Route exact path="/mobile-select" element={<MobileDeviceSelect />} />
                           <Route exact path="/attachments/:context/:key" element={<Attachments />} />
                           {_.map(routes, (rt) => {
                              if (rt.key) return rt;
                           })}
                           <Route exact path="/profile" element={<UserProfile />} />
                           <Route exact path="/login" element={<Navigate to="/" />} />
                           {/* <Route exact path="/reports" element={<Reports />} /> */}
                           <Route exact path="/*" element={<Home />} />
                        </Routes>
                     )}
                  </>
               ) : (
                  // all unauthorized routes
                  <Routes>
                     <Route path="/login" element={<SignIn />} />
                     <Route path="/forgot_password" element={<ForgotPassword />} />
                     <Route path="/scan_login" element={<SignInQRCode />} />
                     <Route path="/reset_password/:resetToken" element={<ResetPassword />} />
                     <Route exact path="/apptest" element={<MobileTest />} />
                     <Route exact path="/*" element={<SignIn />} />
                     <Route exact path="/" element={<SignIn />} />
                  </Routes>
               )}
            </div>
            <Footer />
         </UserContext.Provider>
      </div>
   );
}
